// src/SettingsTimezone.js
import React, { useEffect, useState } from 'react';
import API from './api';

function ParentEmailPreview({
  brandName,
  timezone,
  includeActions,
  extraMessage,
  enabled,
  reminderType = '24h',
}) {
  const brand = (brandName || '').trim() || 'EarlyCare';
  const tz = timezone || 'America/Los_Angeles';

  const studentLabel = 'Doe, Ava';
  const providerName = 'Jane Smith';
  const whenText = `Jan 15, 2025, 09:00 AM (${tz})`;
  const providerPhone = '(555) 123-4567';
  const parentName = 'Jane Doe';

  const typeLabel = reminderType === '24h' ? '24-hour' : '1-hour';
  const subjectTag = reminderType === '24h' ? '24h' : '1h';

  const subjectText = includeActions
    ? `Reminder[${subjectTag}]: Session with [${providerName}] from [${brand}] – please confirm for ${studentLabel}`
    : `Reminder[${subjectTag}]: Session with [${providerName}] from [${brand}] - ${studentLabel}`;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold text-gray-800">
          Parent email preview
        </h4>
        {!enabled && (
          <span className="text-xs text-orange-600">
            Parent notifications are currently disabled – this is only a visual preview.
          </span>
        )}
      </div>

      <div
        className={
          'border rounded-md bg-gray-50 p-4 text-sm text-gray-800' +
          (enabled ? '' : ' opacity-60')
        }
      >
        {/* "Email window" look */}
        <div className="border-b pb-2 mb-3 flex flex-col gap-1">
          <div>
            <span className="font-semibold">From:</span>{' '}
            <span>{brand} &lt;no-reply@example.com&gt;</span>
          </div>
          <div>
            <span className="font-semibold">To:</span>{' '}
            <span>Parent &lt;parent@example.com&gt;</span>
          </div>
          <div>
            <span className="font-semibold">Subject:</span>{' '}
            <span>{subjectText}</span>
          </div>
        </div>

        <div className="leading-relaxed">
          <p className="mb-2">Dear {parentName},</p>
          <p className="mb-2">
            This is a {typeLabel} reminder for your scheduled visit:
          </p>
          <ul className="list-disc list-inside mb-3">
            <li>
              <b>Student:</b> {studentLabel}
            </li>
            <li>
              <b>Educator:</b> {providerName} from {brand} (phone: {providerPhone})
            </li>
            <li>
              <b>Date/Time:</b> {whenText}
            </li>
          </ul>

          {includeActions ? (
            <>
              <p className="mb-2">Please confirm:</p>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold text-white bg-green-700 cursor-default"
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold text-white bg-red-700 cursor-default"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                If the buttons don&apos;t work, parents will also receive plain links
                for Accept / Cancel in the email footer.
              </p>
            </>
          ) : (
            <p className="mb-3">
              This email is for your information only. No confirmation is required at
              this time.
            </p>
          )}

          {extraMessage && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">
                Additional note:
              </p>
              <div
                className="text-sm text-gray-800 border-l-4 border-blue-300 pl-3"
                style={{ whiteSpace: 'pre-line' }}
              >
                {extraMessage}
              </div>
            </div>
          )}

          <p className="mt-4">
            Thank you,
            <br />
            Scheduling Department / {brand}
          </p>
        </div>
      </div>
    </div>
  );
}

function SettingsTimezone() {
  const [timezones, setTimezones] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingTz, setSavingTz] = useState(false);

  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('18:00');
  const [savingWd, setSavingWd] = useState(false);

  const [parentNotifEnabled, setParentNotifEnabled] = useState(false);
  const [parentNotifIncludeActions, setParentNotifIncludeActions] =
    useState(true);
  const [parentNotifExtraMessage, setParentNotifExtraMessage] =
    useState('');
  const [savingParentNotif, setSavingParentNotif] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);

  // logo
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandLogoVer, setBrandLogoVer] = useState(1); // za cache-busting
  const [logoUploadFile, setLogoUploadFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [successMsg, setSuccessMsg] = useState('');

  // Preview reminder type: '24h' or '1h'
  const [previewType, setPreviewType] = useState('24h');

  // Mailing list modal state
  const [mailingModalOpen, setMailingModalOpen] = useState(false);
  const [mailingParents, setMailingParents] = useState([]);
  const [mailingLoading, setMailingLoading] = useState(false);
  const [mailingSaving, setMailingSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // timezone
        const tz = await API.get('/timezone-settings');
        setTimezones(tz.data.timezones || []);
        setSelected(tz.data.selected || '');

        // workday
        const wd = await API.get('/workday-settings');
        setStart((wd.data?.start || '08:00').slice(0, 5));
        setEnd((wd.data?.end || '18:00').slice(0, 5));

        // parent notifications
        const pn = await API.get('/parent-notifications-settings');
        setParentNotifEnabled(!!pn.data.enabled);
        setParentNotifIncludeActions(
          pn.data.include_actions !== undefined
            ? !!pn.data.include_actions
            : true
        );
        setParentNotifExtraMessage(pn.data.extra_message || '');

        // brand name
        try {
          const b = await API.get('/brand-settings');
          setBrandName(b.data?.brand_name || '');
        } catch (e) {
          console.warn(
            'No brand settings yet',
            e?.response?.data || e.message
          );
        }

        // brand logo (samo proveravamo da li postoji neki aktivan logo)
        try {
          const logoRes = await API.get('/brand-logo');
          const hasLogo =
            !!(logoRes.data?.logo_url ||
               logoRes.data?.brand_logo_path ||
               logoRes.data?.has_logo);
          if (hasLogo) {
            // ZNAČAJNO: koristimo BE endpoint koji vraća sliku
            setBrandLogoUrl('/api/brand-logo/image');
          } else {
            setBrandLogoUrl('');
          }
        } catch (e) {
          console.warn(
            'No brand logo yet',
            e?.response?.data || e.message
          );
          setBrandLogoUrl('');
        }
      } catch (e) {
        console.error(e);
        alert('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // central toast helper
  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 2000);
  };

  const saveTimezone = async () => {
    if (!selected) return;
    setSavingTz(true);
    try {
      await API.post('/timezone-settings', { timezone: selected });
      flash('Timezone updated!');
    } catch {
      alert('Failed to save timezone');
    } finally {
      setSavingTz(false);
    }
  };

  const saveWorkday = async () => {
    setSavingWd(true);
    try {
      await API.post('/workday-settings', { start, end });
      flash('Workday updated!');
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save workday');
    } finally {
      setSavingWd(false);
    }
  };

  const saveParentNotif = async () => {
    setSavingParentNotif(true);
    try {
      await API.post('/parent-notifications-settings', {
        enabled: parentNotifEnabled,
        include_actions: parentNotifIncludeActions,
        extra_message: parentNotifExtraMessage,
      });
      flash('Parent notifications updated!');
    } catch {
      alert('Failed to save parent notifications setting');
    } finally {
      setSavingParentNotif(false);
    }
  };

  const saveBrand = async () => {
    const name = (brandName || '').trim();
    if (!name) {
      alert('Brand name is required');
      return;
    }
    setSavingBrand(true);
    try {
      await API.post('/brand-settings', { brand_name: name });
      flash('Brand name updated!');
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save brand name');
    } finally {
      setSavingBrand(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setLogoUploadFile(file || null);
  };

  const saveBrandLogo = async () => {
    if (!logoUploadFile) {
      alert('Please choose a PNG or JPG logo file first.');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoUploadFile);

      await API.post('/brand-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Ignorišemo putanju iz odgovora, uvek koristimo univerzalni image endpoint
      setBrandLogoUrl('/api/brand-logo/image');
      setBrandLogoVer(v => v + 1); // natera browser da povuče novu verziju
      setLogoUploadFile(null);
      flash('Brand logo updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // ---- Mailing list handlers ----

  const openMailingModal = async () => {
    setMailingModalOpen(true);
    setMailingLoading(true);
    try {
      const res = await API.get('/parent-mailing-list');
      setMailingParents(res.data || []);
    } catch (e) {
      console.error(e);
      alert('Failed to load parent mailing list');
      setMailingModalOpen(false);
    } finally {
      setMailingLoading(false);
    }
  };

  const toggleParentMailing = (parentId) => {
    setMailingParents((prev) =>
      prev.map((p) =>
        p.parent_id === parentId
          ? {
              ...p,
              receive_visit_notifications: p.receive_visit_notifications ? 0 : 1,
            }
          : p
      )
    );
  };

  const saveMailingModal = async () => {
    setMailingSaving(true);
    try {
      await API.post('/parent-mailing-list', mailingParents);
      flash('Parent mailing list updated!');
      setMailingModalOpen(false);
    } catch (e) {
      console.error(e);
      alert('Failed to save parent mailing list');
    } finally {
      setMailingSaving(false);
    }
  };

  return (
    <>
      <div className="w-full max-w-4xl mx-auto bg-white p-1 rounded shadow mt-1 space-y-6">
        <div className="text-center mb-2">
          <h2 className="text-xl font-bold text-blue-800">System Settings</h2>
          <p className="text-xs text-gray-500">
            Branding, timezone &amp; parent email notifications.
          </p>
        </div>

        {/* BRANDING (name + logo) */}
        <div className="border border-blue-100 rounded-lg p-4 bg-blue-50/40">
          <h3 className="font-semibold mb-3 text-sm text-blue-900">
            Branding (name &amp; logo)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-4 items-center">
            {/* Brand name kolona */}
            <div>
              <p className="text-xs text-gray-600 mb-1">
                This name will be used in all outgoing emails and reports.
              </p>
              <input
                type="text"
                className="border p-2 rounded w-full"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="e.g. EarlyCare, TIP Infant Services..."
                disabled={loading || savingBrand}
              />
              <button
                className={`mt-2 bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                  savingBrand ? 'opacity-50 pointer-events-none' : ''
                }`}
                onClick={saveBrand}
                disabled={savingBrand}
              >
                {savingBrand ? 'Saving...' : 'Save brand name'}
              </button>
            </div>

            {/* Logo kolona */}
            <div className="flex flex-col items-center md:items-start gap-2">
              <div className="w-32 h-16 border border-dashed border-blue-300 bg-white rounded flex items-center justify-center overflow-hidden">
                {brandLogoUrl ? (
                  <img
                    src={`${brandLogoUrl}?v=${brandLogoVer}`}
                    alt="Brand logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-[10px] text-blue-400 text-center px-2">
                    No logo uploaded
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 text-center md:text-left">
                PNG or JPG, preferably horizontal. Used on Student Progress and Teacher Work reports.
              </p>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleLogoFileChange}
                className="text-xs"
                disabled={uploadingLogo || loading}
              />
              <button
                type="button"
                className={`mt-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs ${
                  uploadingLogo ? 'opacity-50 pointer-events-none' : ''
                }`}
                onClick={saveBrandLogo}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload logo'}
              </button>
            </div>
          </div>
        </div>

        {/* TIMEZONE + WORKDAY u dve kartice */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Timezone */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold mb-2 text-sm text-gray-900">Timezone</h3>
            <select
              className="border p-2 rounded w-full mb-3 text-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Choose timezone --</option>
              {timezones.map((z) => (
                <option key={z.id} value={z.name}>
                  {z.name}
                </option>
              ))}
            </select>
            <button
              className={`bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                !selected || savingTz ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={saveTimezone}
              disabled={!selected || savingTz}
            >
              {savingTz ? 'Saving...' : 'Save timezone'}
            </button>
          </div>

          {/* Workday */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold mb-2 text-sm text-gray-900">Default workday</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Start
                </label>
                <input
                  type="time"
                  className="border p-2 rounded w-full text-sm"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  End
                </label>
                <input
                  type="time"
                  className="border p-2 rounded w-full text-sm"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <button
              className={`mt-3 bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                savingWd ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={saveWorkday}
              disabled={savingWd}
            >
              {savingWd ? 'Saving...' : 'Save workday'}
            </button>
          </div>
        </div>

        {/* Parent notifications kartica */}
        <div className="border border-green-200 rounded-lg p-4 bg-green-50/60">
          <h3 className="font-semibold mb-2 text-sm text-green-900">
            Parent visit confirmations
          </h3>

          {/* Glavni on/off */}
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={parentNotifEnabled}
              onChange={(e) =>
                setParentNotifEnabled(e.target.checked)
              }
              disabled={savingParentNotif || loading}
            />
            <span className="text-sm text-gray-700">
              If enabled, parents/guardians with an email address
              will receive reminder emails 24 hours and 1 hour
              before scheduled visits.
            </span>
          </label>

          {/* Opcije za email sadržaj */}
          <div className="ml-6 mt-2 space-y-3">
            {/* Include Accept/Cancel buttons */}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={parentNotifIncludeActions}
                onChange={(e) =>
                  setParentNotifIncludeActions(
                    e.target.checked
                  )
                }
                disabled={savingParentNotif || loading}
              />
              <span className="text-sm text-gray-700">
                Include <b>Accept</b> / <b>Cancel</b> buttons in
                parent emails. If disabled, emails will be
                informational only.
              </span>
            </label>

            {/* Dodatna napomena u email-u */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Additional note for parents (optional)
              </label>
              <textarea
                className="border p-2 rounded w-full text-sm"
                rows={4}
                value={parentNotifExtraMessage}
                onChange={(e) =>
                  setParentNotifExtraMessage(e.target.value)
                }
                placeholder="E.g. contact details, rescheduling policy, or a general note for families..."
                disabled={savingParentNotif || loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                This note will be added near the bottom of all
                parent reminder emails.
              </p>
            </div>
          </div>

          <button
            className={`mt-3 bg-green-700 text-white px-4 py-2 rounded text-sm ${
              savingParentNotif ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={saveParentNotif}
            disabled={savingParentNotif}
          >
            {savingParentNotif ? 'Saving...' : 'Save parent notifications'}
          </button>

          {/* Dugme za mailing list modal */}
          <div className="mt-2">
            <button
              type="button"
              className="text-xs text-green-800 underline"
              onClick={openMailingModal}
            >
              Manage parent email list (opt-in/out)
            </button>
          </div>

          {/* PREVIEW TYPE TOGGLE */}
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-700">
            <span className="font-semibold">
              Preview reminder type:
            </span>
            <button
              type="button"
              onClick={() => setPreviewType('24h')}
              className={
                'px-3 py-1 rounded border text-xs ' +
                (previewType === '24h'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300')
              }
            >
              24h
            </button>
            <button
              type="button"
              onClick={() => setPreviewType('1h')}
              className={
                'px-3 py-1 rounded border text-xs ' +
                (previewType === '1h'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300')
              }
            >
              1h
            </button>
          </div>

          {/* LIVE PREVIEW */}
          <ParentEmailPreview
            brandName={brandName}
            timezone={selected}
            includeActions={parentNotifIncludeActions}
            extraMessage={parentNotifExtraMessage}
            enabled={parentNotifEnabled}
            reminderType={previewType}
          />
        </div>

        {/* MAILING LIST MODAL */}
        {mailingModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-lg p-4 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">
                  Parent email list (visit notifications)
                </h3>
                <button
                  className="text-xs text-gray-500"
                  onClick={() => setMailingModalOpen(false)}
                >
                  ✕ Close
                </button>
              </div>

              <p className="text-xs text-gray-600 mb-2">
                Only parents checked below will receive visit reminder emails.
                Others will be excluded from these notifications.
              </p>

              {mailingLoading ? (
                <div className="text-sm text-gray-600">Loading...</div>
              ) : (
                <table className="w-full text-xs border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-1 text-left">Receive</th>
                      <th className="border p-1 text-left">Parent</th>
                      <th className="border p-1 text-left">Email</th>
                      <th className="border p-1 text-left">Students</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mailingParents.map((p) => (
                      <tr key={p.parent_id}>
                        <td className="border p-1 text-center">
                          <input
                            type="checkbox"
                            checked={!!p.receive_visit_notifications}
                            onChange={() =>
                              toggleParentMailing(p.parent_id)
                            }
                          />
                        </td>
                        <td className="border p-1">
                          {p.last_name ? `${p.last_name}, ` : ''}
                          {p.first_name}
                        </td>
                        <td className="border p-1">{p.email}</td>
                        <td className="border p-1">
                          {p.students_label}
                        </td>
                      </tr>
                    ))}
                    {!mailingParents.length && (
                      <tr>
                        <td
                          colSpan={4}
                          className="border p-2 text-center text-gray-500"
                        >
                          No parents found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              <div className="mt-3 flex justify-end gap-2">
                <button
                  className="px-3 py-1 text-xs border rounded"
                  onClick={() => setMailingModalOpen(false)}
                  disabled={mailingSaving}
                >
                  Cancel
                </button>
                <button
                  className={
                    'px-3 py-1 text-xs rounded text-white bg-blue-600 ' +
                    (mailingSaving
                      ? 'opacity-50 pointer-events-none'
                      : '')
                  }
                  onClick={saveMailingModal}
                  disabled={mailingSaving}
                >
                  {mailingSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CENTRAL TOAST MODAL (white, blue text, bigger) */}
      {successMsg && (
        <div className="fixed inset-0 flex items-center justify-center z-[60]">
          {/* lagani blur/overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-20" />
          <div className="relative bg-white text-blue-700 text-base px-6 py-4 rounded-lg shadow-2xl border border-blue-200 max-w-md w-[90%] text-center">
            <div className="font-semibold mb-1">Settings saved</div>
            <div>{successMsg}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default SettingsTimezone;


